import { useCallback, useState } from 'react';

import { API_URL } from 'constant';
import { useNetwork } from 'hooks';

const GENERIC_ERROR = 'Verification failed — try again';

export interface IFaceVerified {
	ok: true;
	token: string;
	enrollId: string;
}

export interface IFaceVerificationFailed {
	ok: false;
	code: 'FACE_NOT_FOUND' | 'FACE_SERVICE_UNAVAILABLE' | 'error';
	message: string;
}

export type IFaceVerificationResult = IFaceVerified | IFaceVerificationFailed;

/**
 * `POST /face-verifications` — enrols the caller's face for one pending
 * transfer and answers the short-lived token `commit` needs.
 *
 * Goes through `useNetwork({returnResponse: true})` rather than the default
 * `post` — the same flag `useEvaultApi` is built on (see its doc comment):
 * with it, a failure hands back `{errorCode, errorData}` BEFORE `useNetwork`
 * reaches `errorNotification`, so a 409 `FACE_NOT_FOUND` and a 503
 * `FACE_SERVICE_UNAVAILABLE` arrive with their status intact and raise no
 * toast — the caller (`transfer-modal.tsx`) renders its own copy per `code`
 * inline instead. That is the existing sibling option `network.ts` already
 * has for this; no second HTTP client. This endpoint takes the ordinary session bearer, not the
 * evault-scoped one `useEvaultApi` mints, so it is a plain `useNetwork` call.
 */
export const useFaceVerification = () => {
	const { post } = useNetwork({ updateState: false, returnResponse: true });
	const [busy, setBusy] = useState(false);

	const verify = useCallback(
		async (transferId: string, images: string[]): Promise<IFaceVerificationResult> => {
			setBusy(true);
			const result = await post(API_URL.FACE_VERIFICATIONS, { transferId, images });
			setBusy(false);

			if (result?.errorCode) {
				const status = Number(result.errorCode);
				const message: string = result.errorData?.message || GENERIC_ERROR;
				if (status === 409) return { ok: false, code: 'FACE_NOT_FOUND', message };
				if (status === 503) {
					return { ok: false, code: 'FACE_SERVICE_UNAVAILABLE', message };
				}
				return { ok: false, code: 'error', message };
			}

			if (!result?.token) return { ok: false, code: 'error', message: GENERIC_ERROR };
			return { ok: true, token: result.token, enrollId: result.enrollId ?? '' };
		},
		[post]
	);

	return { verify, busy };
};
